import { envAttack, envRelease, holdAt, midiToFreq, ModSources, velocityGain, type Instrument } from './instrument';
import type { FmPreset, SynthPreset } from './presets';

const MAX_VOICES = 16;

interface Voice {
  note: number;
  held: boolean;
  sustained: boolean;
  released: boolean;
  release(t: number, fast?: boolean): void;
}

/** Voice allocation, sustain pedal and wheels shared by the synth engines. */
abstract class PolyInstrument<V extends Voice> implements Instrument {
  protected voices: V[] = [];
  protected sustain = false;
  protected readonly out: GainNode;

  constructor(
    protected ctx: AudioContext,
    protected mods: ModSources,
    gain: number,
    destination: AudioNode,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = gain;
    this.out.connect(destination);
  }

  protected abstract createVoice(note: number, velocity: number, t: number): V;

  noteOn(note: number, velocity: number, t: number): void {
    for (const v of this.voices) if (v.note === note && !v.released) this.releaseVoice(v, t, true);
    this.voices.push(this.createVoice(note, velocity, t));
    if (this.voices.length > MAX_VOICES) {
      // Prefer a voice that's already releasing, and fade it out fast: it
      // may still be ringing a long release tail.
      const victim = this.voices.find((v) => v.released) ?? this.voices[0];
      victim.released = true;
      victim.held = false;
      victim.sustained = false;
      victim.release(t, true);
      this.voices = this.voices.filter((v) => v !== victim);
    }
  }

  noteOff(note: number, t: number): void {
    for (const v of this.voices) {
      if (v.note !== note || !v.held) continue;
      v.held = false;
      if (this.sustain) v.sustained = true;
      else this.releaseVoice(v, t);
    }
  }

  setSustain(on: boolean, t: number): void {
    this.sustain = on;
    if (on) return;
    for (const v of this.voices) if (v.sustained && !v.held) this.releaseVoice(v, t);
  }

  allNotesOff(t: number): void {
    this.sustain = false;
    for (const v of this.voices) this.releaseVoice(v, t, true);
  }

  setBend(cents: number, t: number): void {
    this.mods.setBend(cents, t);
  }

  setMod(amount: number, t: number): void {
    this.mods.setMod(amount, t);
  }

  dispose(): void {
    const t = this.ctx.currentTime;
    this.allNotesOff(t);
    this.mods.dispose(t + 0.5);
    setTimeout(() => this.out.disconnect(), 600);
  }

  protected releaseVoice(v: V, t: number, fast = false): void {
    if (v.released) return;
    v.released = true;
    v.held = false;
    v.sustained = false;
    v.release(t, fast);
  }

  protected forget(v: V): void {
    this.voices = this.voices.filter((x) => x !== v);
  }
}

let noiseBuffer: AudioBuffer | null = null;
function getNoise(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const d = noiseBuffer.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return noiseBuffer;
}

interface SynthVoice extends Voice {
  oscs: OscillatorNode[];
  freqs: number[];
}

export class Synth extends PolyInstrument<SynthVoice> {
  private waves = new Map<number, PeriodicWave>();
  private stack: number[] = [];
  private monoVoice: SynthVoice | null = null;

  constructor(ctx: AudioContext, private preset: SynthPreset, destination: AudioNode) {
    super(ctx, new ModSources(ctx, preset.lfo), preset.gain, destination);
  }

  override noteOn(note: number, velocity: number, t: number): void {
    if (!this.preset.mono) return super.noteOn(note, velocity, t);
    this.stack = this.stack.filter((n) => n !== note);
    this.stack.push(note);
    const mv = this.monoVoice;
    if (mv && !mv.released) {
      this.glide(mv, note, t);
      mv.held = true;
      mv.sustained = false;
      return;
    }
    const v = this.createVoice(note, velocity, t);
    this.voices.push(v);
    this.monoVoice = v;
  }

  override noteOff(note: number, t: number): void {
    if (!this.preset.mono) return super.noteOff(note, t);
    this.stack = this.stack.filter((n) => n !== note);
    const mv = this.monoVoice;
    if (!mv || mv.released || mv.note !== note) return;
    if (this.stack.length) {
      this.glide(mv, this.stack[this.stack.length - 1], t);
    } else if (this.sustain) {
      mv.held = false;
      mv.sustained = true;
    } else {
      this.releaseVoice(mv, t);
    }
  }

  override allNotesOff(t: number): void {
    this.stack = [];
    super.allNotesOff(t);
  }

  private glide(v: SynthVoice, note: number, t: number): void {
    v.note = note;
    const tau = Math.max(0.001, (this.preset.glide ?? 0) / 3);
    v.oscs.forEach((osc, i) => {
      const f = this.oscFreq(note, i);
      holdAt(osc.frequency, t);
      osc.frequency.setTargetAtTime(f, t, tau);
      v.freqs[i] = f;
    });
  }

  private oscFreq(note: number, i: number): number {
    const spec = this.preset.osc[i];
    return midiToFreq(note + ((this.preset.octave ?? 0) + (spec.octave ?? 0)) * 12);
  }

  private wave(partials: number[]): PeriodicWave {
    const key = partials.length;
    let w = this.waves.get(key);
    if (!w) {
      const real = new Float32Array(partials.length + 1);
      const imag = new Float32Array(partials.length + 1);
      partials.forEach((a, i) => (imag[i + 1] = a));
      w = this.ctx.createPeriodicWave(real, imag);
      this.waves.set(key, w);
    }
    return w;
  }

  protected createVoice(note: number, velocity: number, t: number): SynthVoice {
    const { ctx, preset } = this;
    const p = preset;
    const n = note + (p.octave ?? 0) * 12;
    const v = velocity / 127;

    const filter = ctx.createBiquadFilter();
    filter.type = p.filter.type;
    filter.Q.value = p.filter.q;
    const nyquist = ctx.sampleRate / 2 - 100;
    const base = Math.min(nyquist, p.filter.cutoff * Math.pow(2, ((n - 60) / 12) * p.filter.keyTrack));
    const depth = p.filter.env * (1 - p.filter.velocity + p.filter.velocity * v);
    const peak = Math.min(nyquist, base * Math.pow(2, depth));
    const sus = Math.min(nyquist, base * Math.pow(2, depth * p.filterEnv.s));
    filter.frequency.setValueAtTime(base, t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(base + 1, peak), t + Math.max(0.002, p.filterEnv.a));
    filter.frequency.setTargetAtTime(sus, t + Math.max(0.002, p.filterEnv.a), Math.max(0.005, p.filterEnv.d / 3));
    this.mods.lfoFilter.connect(filter.detune);

    const amp = ctx.createGain();
    envAttack(amp.gain, p.amp, velocityGain(velocity), t);
    filter.connect(amp);
    amp.connect(this.out);

    const freqs: number[] = [];
    const oscs = p.osc.map((spec, i) => {
      const osc = ctx.createOscillator();
      if (spec.partials) osc.setPeriodicWave(this.wave(spec.partials));
      else osc.type = spec.wave;
      const f = this.oscFreq(note, i);
      freqs.push(f);
      osc.frequency.value = f;
      osc.detune.value = (spec.detune ?? 0) + (Math.random() * 2 - 1) * (p.drift ?? 0);
      this.mods.connectPitch(osc.detune);
      const g = ctx.createGain();
      g.gain.value = spec.level;
      osc.connect(g);
      g.connect(filter);
      osc.start(t);
      return osc;
    });

    let noise: AudioBufferSourceNode | null = null;
    if (p.noise) {
      noise = ctx.createBufferSource();
      noise.buffer = getNoise(ctx);
      noise.loop = true;
      const g = ctx.createGain();
      g.gain.value = p.noise;
      noise.connect(g);
      g.connect(filter);
      noise.start(t, Math.random() * 1.5);
    }

    const voice: SynthVoice = {
      note,
      held: true,
      sustained: false,
      released: false,
      oscs,
      freqs,
      release: (rt, fast) => {
        const r = fast ? 0.02 : p.amp.r;
        envRelease(amp.gain, r, rt);
        holdAt(filter.frequency, rt);
        filter.frequency.setTargetAtTime(base, rt, Math.max(0.005, p.filterEnv.r / 4));
        const end = rt + r * 2 + 0.05;
        for (const o of oscs) o.stop(end);
        noise?.stop(end);
      },
    };
    oscs[0].onended = () => {
      for (const o of oscs) this.mods.disconnectPitch(o.detune);
      this.mods.disconnectFilter(filter.detune);
      amp.disconnect();
      filter.disconnect();
      this.forget(voice);
      if (this.monoVoice === voice) this.monoVoice = null;
    };
    return voice;
  }
}

export class FmSynth extends PolyInstrument<Voice> {
  constructor(ctx: AudioContext, private preset: FmPreset, destination: AudioNode) {
    super(
      ctx,
      new ModSources(ctx, { rate: preset.vibrato?.rate ?? 5, pitch: preset.vibrato?.cents ?? 0, filter: 0 }),
      preset.gain,
      destination,
    );
  }

  protected createVoice(note: number, velocity: number, t: number): Voice {
    const { ctx, preset: p } = this;
    const f = midiToFreq(note + (p.octave ?? 0) * 12);
    const v = velocity / 127;
    const amp = ctx.createGain();
    envAttack(amp.gain, p.amp, velocityGain(velocity), t);
    amp.connect(this.out);

    const oscs: OscillatorNode[] = [];
    for (const pair of p.pairs) {
      const carrier = ctx.createOscillator();
      carrier.frequency.value = f * pair.carrier;
      const mod = ctx.createOscillator();
      mod.frequency.value = f * pair.modulator;
      this.mods.connectPitch(carrier.detune);
      this.mods.connectPitch(mod.detune);
      const modGain = ctx.createGain();
      // Harder hits are brighter: velocity scales the modulation index.
      const scale = f * pair.modulator * (0.45 + 0.55 * v);
      modGain.gain.setValueAtTime(pair.index * scale, t);
      modGain.gain.setTargetAtTime(pair.sustainIndex * scale, t, Math.max(0.005, pair.decay / 3));
      mod.connect(modGain);
      modGain.connect(carrier.frequency);
      const level = ctx.createGain();
      level.gain.value = pair.level;
      carrier.connect(level);
      level.connect(amp);
      carrier.start(t);
      mod.start(t);
      oscs.push(carrier, mod);
    }

    const voice: Voice = {
      note,
      held: true,
      sustained: false,
      released: false,
      release: (rt, fast) => {
        const r = fast ? 0.02 : p.amp.r;
        envRelease(amp.gain, r, rt);
        for (const o of oscs) o.stop(rt + r * 2 + 0.05);
      },
    };
    // A decaying envelope (sustain 0) finishes on its own; free the voice then.
    if (p.amp.s === 0) {
      for (const o of oscs) o.stop(t + p.amp.a + p.amp.d * 2.5 + 0.1);
    }
    oscs[0].onended = () => {
      for (const o of oscs) this.mods.disconnectPitch(o.detune);
      amp.disconnect();
      this.forget(voice);
    };
    return voice;
  }
}
